import React, { useState, useEffect, useCallback } from 'react';
import api, { API_BASE } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  DownloadSimple, FunnelSimple, ChartBar, Play, FloppyDisk,
  Trash, BookmarkSimple, CaretDown, CaretUp
} from '@phosphor-icons/react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import { toast } from 'sonner';

const fmt = (n) => new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP', minimumFractionDigits: 0 }).format(n || 0);
const PIE_COLORS = ['#0055FF','#22C55E','#EAB308','#EF4444','#8B5CF6','#EC4899','#06B6D4','#F97316','#6366F1','#14B8A6'];

export default function ReportsPage() {
  const [reportTypes, setReportTypes] = useState([]);
  const [selectedType, setSelectedType] = useState('ytd_expenses');
  const [filters, setFilters] = useState({ date_from: '', date_to: '', vendor_id: '', category: '', status: '', amount_min: '', amount_max: '' });
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [presets, setPresets] = useState({ user_presets: [], system_presets: [] });
  const [showSavePreset, setShowSavePreset] = useState(false);
  const [presetName, setPresetName] = useState('');

  const CATEGORIES = ["Utilities","Rent","Professional Fees","Supplies","Repairs and Maintenance","Taxes and Government Fees","Contractor / Service Provider","Marketing","Travel","Others"];

  useEffect(() => {
    api.get('/reports/types').then(r => setReportTypes(r.data));
    api.get('/vendors', { params: { status: 'active', limit: 500 } }).then(r => setVendors(r.data.items));
    api.get('/reports/presets').then(r => setPresets(r.data)).catch(() => {});
  }, []);

  const generateReport = useCallback(async () => {
    setLoading(true);
    try {
      const payload = { report_type: selectedType };
      if (filters.date_from) payload.date_from = filters.date_from;
      if (filters.date_to) payload.date_to = filters.date_to;
      if (filters.vendor_id) payload.vendor_id = filters.vendor_id;
      if (filters.category) payload.category = filters.category;
      if (filters.status && filters.status !== 'all') payload.status = filters.status;
      if (filters.amount_min) payload.amount_min = parseFloat(filters.amount_min);
      if (filters.amount_max) payload.amount_max = parseFloat(filters.amount_max);
      const { data } = await api.post('/reports/generate', payload);
      setReportData(data);
      setFiltersExpanded(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate report');
    } finally { setLoading(false); }
  }, [selectedType, filters]);

  const exportReport = async (format) => {
    try {
      const payload = { report_type: selectedType };
      if (filters.date_from) payload.date_from = filters.date_from;
      if (filters.date_to) payload.date_to = filters.date_to;
      if (filters.vendor_id) payload.vendor_id = filters.vendor_id;
      if (filters.category) payload.category = filters.category;
      if (filters.status && filters.status !== 'all') payload.status = filters.status;
      if (filters.amount_min) payload.amount_min = parseFloat(filters.amount_min);
      if (filters.amount_max) payload.amount_max = parseFloat(filters.amount_max);
      const resp = await api.post(`/reports/export?format=${format}`, payload, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_${selectedType}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Report exported as ${format.toUpperCase()}`);
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const savePreset = async () => {
    if (!presetName.trim()) { toast.error('Enter a preset name'); return; }
    try {
      const cleanFilters = {};
      if (filters.date_from) cleanFilters.date_from = filters.date_from;
      if (filters.date_to) cleanFilters.date_to = filters.date_to;
      if (filters.vendor_id) cleanFilters.vendor_id = filters.vendor_id;
      if (filters.category) cleanFilters.category = filters.category;
      if (filters.status) cleanFilters.status = filters.status;
      if (filters.amount_min) cleanFilters.amount_min = filters.amount_min;
      if (filters.amount_max) cleanFilters.amount_max = filters.amount_max;
      await api.post('/reports/presets', { name: presetName, report_type: selectedType, filters: cleanFilters });
      toast.success('Preset saved');
      setShowSavePreset(false);
      setPresetName('');
      api.get('/reports/presets').then(r => setPresets(r.data));
    } catch (err) { toast.error('Failed to save preset'); }
  };

  const loadPreset = (preset) => {
    setSelectedType(preset.report_type);
    setFilters({ date_from: preset.filters?.date_from || '', date_to: preset.filters?.date_to || '', vendor_id: preset.filters?.vendor_id || '', category: preset.filters?.category || '', status: preset.filters?.status || '', amount_min: preset.filters?.amount_min || '', amount_max: preset.filters?.amount_max || '' });
    toast.success(`Loaded preset: ${preset.name}`);
  };

  const deletePreset = async (id) => {
    try {
      await api.delete(`/reports/presets/${id}`);
      toast.success('Preset deleted');
      api.get('/reports/presets').then(r => setPresets(r.data));
    } catch (err) { toast.error('Failed to delete'); }
  };

  const currentTypeInfo = reportTypes.find(t => t.id === selectedType);

  return (
    <div className="space-y-4" data-testid="reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Reports</h1>
          <p className="text-sm text-[#475569] mt-1">Generate, filter, and export finance and operations reports</p>
        </div>
        <div className="flex gap-2">
          {reportData && (
            <>
              <Button variant="outline" className="rounded-sm gap-2 text-sm" onClick={() => exportReport('excel')} data-testid="export-excel-btn">
                <DownloadSimple size={14} /> Excel
              </Button>
              <Button variant="outline" className="rounded-sm gap-2 text-sm" onClick={() => exportReport('pdf')} data-testid="export-pdf-btn">
                <DownloadSimple size={14} /> PDF
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Left panel: Report type + Filters + Presets */}
        <div className="space-y-4">
          {/* Report Type */}
          <Card className="rounded-sm border">
            <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-[0.1em] text-[#475569] font-semibold">Report Type</CardTitle></CardHeader>
            <CardContent className="p-2 space-y-0.5">
              {reportTypes.map(rt => (
                <button
                  key={rt.id}
                  onClick={() => { setSelectedType(rt.id); setReportData(null); }}
                  className={`w-full text-left px-3 py-2 rounded-sm text-sm transition-colors ${selectedType === rt.id ? 'bg-[#0055FF]/10 text-[#0055FF] font-medium' : 'text-[#475569] hover:bg-[#F1F5F9]'}`}
                  data-testid={`report-type-${rt.id}`}
                >
                  {rt.name}
                </button>
              ))}
            </CardContent>
          </Card>

          {/* Saved Presets */}
          {(presets.user_presets?.length > 0 || presets.system_presets?.length > 0) && (
            <Card className="rounded-sm border">
              <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-[0.1em] text-[#475569] font-semibold flex items-center gap-1"><BookmarkSimple size={12} /> Saved Presets</CardTitle></CardHeader>
              <CardContent className="p-2 space-y-0.5">
                {presets.user_presets?.map(p => (
                  <div key={p.id} className="flex items-center justify-between group">
                    <button onClick={() => loadPreset(p)} className="flex-1 text-left px-3 py-1.5 rounded-sm text-sm text-[#475569] hover:bg-[#F1F5F9] truncate">{p.name}</button>
                    <button onClick={() => deletePreset(p.id)} className="opacity-0 group-hover:opacity-100 p-1 text-[#94A3B8] hover:text-[#EF4444]"><Trash size={12} /></button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right panel: Filters + Results */}
        <div className="lg:col-span-3 space-y-4">
          {/* Filters */}
          <Card className="rounded-sm border">
            <CardHeader className="pb-2 cursor-pointer" onClick={() => setFiltersExpanded(!filtersExpanded)}>
              <CardTitle className="text-sm font-semibold flex items-center justify-between">
                <span className="flex items-center gap-2"><FunnelSimple size={16} /> Filters {currentTypeInfo && <span className="text-xs font-normal text-[#94A3B8]">— {currentTypeInfo.name}</span>}</span>
                {filtersExpanded ? <CaretUp size={14} /> : <CaretDown size={14} />}
              </CardTitle>
            </CardHeader>
            {filtersExpanded && (
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.05em] text-[#94A3B8]">Date From</Label>
                    <Input type="date" value={filters.date_from} onChange={e => setFilters(f => ({...f, date_from: e.target.value}))} className="rounded-sm mt-1 h-8 text-sm" data-testid="filter-date-from" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.05em] text-[#94A3B8]">Date To</Label>
                    <Input type="date" value={filters.date_to} onChange={e => setFilters(f => ({...f, date_to: e.target.value}))} className="rounded-sm mt-1 h-8 text-sm" data-testid="filter-date-to" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.05em] text-[#94A3B8]">Vendor</Label>
                    <Select value={filters.vendor_id || undefined} onValueChange={v => setFilters(f => ({...f, vendor_id: v}))}>
                      <SelectTrigger className="rounded-sm mt-1 h-8 text-sm" data-testid="filter-vendor"><SelectValue placeholder="All vendors" /></SelectTrigger>
                      <SelectContent className="rounded-sm max-h-60">{vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.05em] text-[#94A3B8]">Category</Label>
                    <Select value={filters.category || undefined} onValueChange={v => setFilters(f => ({...f, category: v}))}>
                      <SelectTrigger className="rounded-sm mt-1 h-8 text-sm" data-testid="filter-category"><SelectValue placeholder="All categories" /></SelectTrigger>
                      <SelectContent className="rounded-sm">{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.05em] text-[#94A3B8]">Status</Label>
                    <Select value={filters.status || undefined} onValueChange={v => setFilters(f => ({...f, status: v}))}>
                      <SelectTrigger className="rounded-sm mt-1 h-8 text-sm" data-testid="filter-status"><SelectValue placeholder="All statuses" /></SelectTrigger>
                      <SelectContent className="rounded-sm">
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="Approved">Approved</SelectItem>
                        <SelectItem value="Rejected">Rejected</SelectItem>
                        <SelectItem value="Pending Accounting Manager Approval">Pending AM</SelectItem>
                        <SelectItem value="Pending General Manager Approval">Pending GM</SelectItem>
                        <SelectItem value="Draft">Draft</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.05em] text-[#94A3B8]">Min Amount</Label>
                    <Input type="number" value={filters.amount_min} onChange={e => setFilters(f => ({...f, amount_min: e.target.value}))} className="rounded-sm mt-1 h-8 text-sm" placeholder="0" data-testid="filter-amount-min" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.05em] text-[#94A3B8]">Max Amount</Label>
                    <Input type="number" value={filters.amount_max} onChange={e => setFilters(f => ({...f, amount_max: e.target.value}))} className="rounded-sm mt-1 h-8 text-sm" placeholder="No limit" data-testid="filter-amount-max" />
                  </div>
                  <div className="flex items-end gap-2">
                    <Button className="rounded-sm bg-[#0055FF] hover:bg-[#0043CC] gap-2 h-8 flex-1" onClick={generateReport} disabled={loading} data-testid="generate-report-btn">
                      <Play size={14} weight="fill" /> {loading ? 'Running...' : 'Generate'}
                    </Button>
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="ghost" size="sm" className="rounded-sm text-xs gap-1" onClick={() => setFilters({ date_from: '', date_to: '', vendor_id: '', category: '', status: '', amount_min: '', amount_max: '' })} data-testid="clear-filters-btn">Clear Filters</Button>
                  <Button variant="ghost" size="sm" className="rounded-sm text-xs gap-1" onClick={() => setShowSavePreset(true)} data-testid="save-preset-btn"><FloppyDisk size={12} /> Save as Preset</Button>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Report Results */}
          {loading && <div className="space-y-4 animate-pulse"><div className="h-32 bg-muted rounded-sm" /><div className="h-64 bg-muted rounded-sm" /></div>}

          {reportData && !loading && (
            <div className="space-y-4" data-testid="report-results">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-heading text-xl font-bold tracking-tight">{reportData.title}</h2>
                  <p className="text-xs text-[#94A3B8]">Generated {reportData.generated_at?.slice(0, 16).replace('T',' ')} by {reportData.generated_by}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="rounded-sm gap-1 text-xs" onClick={() => exportReport('excel')}><DownloadSimple size={12} /> Excel</Button>
                  <Button variant="outline" size="sm" className="rounded-sm gap-1 text-xs" onClick={() => exportReport('pdf')}><DownloadSimple size={12} /> PDF</Button>
                </div>
              </div>

              {/* Summary Cards */}
              {reportData.summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(reportData.summary).filter(([k,v]) => typeof v !== 'object').map(([k, v]) => (
                    <Card key={k} className="rounded-sm border">
                      <CardContent className="p-3">
                        <p className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8]">{k.replace(/_/g, ' ')}</p>
                        <p className="text-xl font-bold mt-0.5">
                          {typeof v === 'number' && (k.includes('total') || k.includes('amount') || k.includes('spend'))
                            ? fmt(v) : typeof v === 'number' && k.includes('hours') ? `${v}h` : v}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Charts for applicable report types */}
              <ReportCharts reportType={selectedType} data={reportData} />

              {/* Data Table */}
              {reportData.rows?.length > 0 && (
                <Card className="rounded-sm border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold">{reportData.rows.length} Records</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-[#F1F5F9]">
                            {Object.keys(reportData.rows[0]).filter(k => !['vendor_id','is_overdue','month_num'].includes(k)).map(h => (
                              <TableHead key={h} className={`text-[10px] uppercase tracking-[0.1em] font-semibold whitespace-nowrap ${(h.includes('amount') || h.includes('year') || h.includes('difference') || h === 'pct') ? 'text-right' : ''}`}>
                                {h.replace(/_/g, ' ')}
                              </TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {reportData.rows.slice(0, 100).map((row, i) => (
                            <TableRow key={i} className={`hover:bg-[#F8FAFC] ${row.is_overdue ? 'bg-[#FEE2E2]/30' : ''}`}>
                              {Object.entries(row).filter(([k]) => !['vendor_id','is_overdue','month_num'].includes(k)).map(([k, v]) => (
                                <TableCell key={k} className={`text-sm whitespace-nowrap ${(k.includes('amount') || k.includes('year') || k.includes('difference')) ? 'text-right font-medium' : ''} ${k === 'change_pct' ? (v >= 0 ? 'text-[#EF4444]' : 'text-[#22C55E]') : ''}`}>
                                  {k.includes('amount') || k.includes('year') || k.includes('difference') || k === 'current_year' || k === 'last_year'
                                    ? fmt(v)
                                    : k === 'change_pct' || k === 'pct'
                                    ? `${v > 0 ? '+' : ''}${v}%`
                                    : k === 'is_recurring'
                                    ? (v ? 'Yes' : 'No')
                                    : v}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    {reportData.rows.length > 100 && (
                      <p className="text-xs text-[#94A3B8] text-center py-2">Showing first 100 of {reportData.rows.length} records. Export for full data.</p>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {!reportData && !loading && (
            <div className="text-center py-16">
              <ChartBar size={48} className="mx-auto text-[#94A3B8] mb-3" />
              <p className="text-[#475569]">Select a report type and click Generate</p>
              <p className="text-xs text-[#94A3B8] mt-1">Apply filters to narrow down results</p>
            </div>
          )}
        </div>
      </div>

      {/* Save Preset Dialog */}
      <Dialog open={showSavePreset} onOpenChange={setShowSavePreset}>
        <DialogContent className="rounded-sm max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Save Report Preset</DialogTitle></DialogHeader>
          <div>
            <Label className="text-xs uppercase tracking-[0.05em] text-[#475569]">Preset Name</Label>
            <Input value={presetName} onChange={e => setPresetName(e.target.value)} placeholder="e.g. Monthly YTD Review" className="rounded-sm mt-1" data-testid="preset-name-input" />
            <p className="text-xs text-[#94A3B8] mt-2">Report: {currentTypeInfo?.name}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setShowSavePreset(false)}>Cancel</Button>
            <Button className="rounded-sm bg-[#0055FF]" onClick={savePreset} data-testid="confirm-save-preset-btn">Save Preset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReportCharts({ reportType, data }) {
  if (!data) return null;

  if (reportType === 'ytd_expenses' && data.by_month) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="rounded-sm border lg:col-span-2">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Monthly Breakdown</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={data.by_month}><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey="month" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} tickFormatter={v => `₱${(v/1000).toFixed(0)}k`} /><Tooltip formatter={v => fmt(v)} /><Area type="monotone" dataKey="amount" stroke="#0055FF" fill="#0055FF" fillOpacity={0.1} /></AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card className="rounded-sm border">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">By Category</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart><Pie data={data.by_category?.slice(0,8)} dataKey="amount" nameKey="category" cx="50%" cy="50%" outerRadius={75} label={({category}) => category?.length > 10 ? category.slice(0,10)+'...' : category}>
                {data.by_category?.slice(0,8).map((_,i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie><Tooltip formatter={v => fmt(v)} /></PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (reportType === 'monthly_trend' && data.trend) {
    return (
      <Card className="rounded-sm border">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Expense Trend</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={data.trend}><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey="label" tick={{ fontSize: 9 }} /><YAxis tick={{ fontSize: 10 }} tickFormatter={v => `₱${(v/1000).toFixed(0)}k`} /><Tooltip formatter={v => fmt(v)} /><Bar dataKey="amount" fill="#0055FF" radius={[2,2,0,0]} /></BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    );
  }

  if (reportType === 'category_breakdown' && data.rows) {
    return (
      <Card className="rounded-sm border">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Category Distribution</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={data.rows} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => `₱${(v/1000).toFixed(0)}k`} /><YAxis type="category" dataKey="category" tick={{ fontSize: 10 }} width={130} /><Tooltip formatter={v => fmt(v)} /><Bar dataKey="amount" fill="#0055FF" radius={[0,2,2,0]} /></BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    );
  }

  if (reportType === 'approval_aging' && data.summary?.aging) {
    const agingData = Object.entries(data.summary.aging).map(([k, v]) => ({ bucket: k, count: v }));
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="rounded-sm border">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Aging Distribution</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={agingData}><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey="bucket" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} /><Tooltip /><Bar dataKey="count" fill="#EAB308" radius={[2,2,0,0]} /></BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card className="rounded-sm border">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Metrics</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center p-4 border rounded-sm">
              <p className="text-3xl font-bold text-[#0055FF]">{data.summary.avg_turnaround_hours}h</p>
              <p className="text-xs text-[#475569] mt-1">Average Approval Turnaround</p>
            </div>
            <div className="text-center p-4 border rounded-sm">
              <p className="text-3xl font-bold text-[#EAB308]">{data.summary.pending_count}</p>
              <p className="text-xs text-[#475569] mt-1">Currently Pending</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
}
